import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useOktaClient, useCountdownTimer, useEnv } from 'hooks';
import { useStoreValue, OKTA_SMS_FACTOR, OKTA_EMAIL_FACTOR } from 'store';
import { Text, Stack } from 'basis';
import { isFeatureOn } from 'featureToggles';
import { TextButton } from 'components/button';
import { DEV_FEATURES } from '__dev/devFeatures';
import { BasisOneTimeCode } from './BasisOneTimeCode';

function getMinutes(seconds) {
  const minutes = Math.floor(seconds / 60);
  const minutesToDisplay = minutes < 10 ? `0${minutes}` : `${minutes}`;
  return minutesToDisplay;
}
function getRemainingSeconds(seconds) {
  const remainingSeconds = seconds % 60;
  const secondsToDisplay = remainingSeconds < 10 ? `0${remainingSeconds}` : `${remainingSeconds}`;
  return secondsToDisplay;
}

export function OneTimeCode({
  resendMsecs,
  onResendCodeClick,
  onResendCodeError,
  onTimerExpired,
  onTimerReset,
  resend,
  formData,
  theme,
  resending,
  ...rest
}) {
  /**
   * type State = 'locked' | 'pending' | 'success' | 'error' | 'ready';
   */
  const [state, setState] = useState('locked');
  const [doesCountDown, setDoesCountDown] = useState(true);
  /**
   * type RequestAfterLock = 'sms' | 'email' | null;
   */
  const requestAfterLock = useRef(null);
  const timeout = useRef(null);
  const prevResending = useRef(resending);
  const [storeState, updateStore] = useStoreValue();
  const factor = storeState.oktaFactor;
  const getEnv = useEnv();
  const defaultTimer = getEnv('VITE_AU_VERIFY_COUNTDOWNTIMER_EXPIRATION_IN_SEC');

  const onTimedOut = () => {
    onTimerExpired && onTimerExpired();
  };

  const handleResendCodeSuccess = () => {
    setState('success');
    resetTimer();
    setDoesCountDown(true);
    onTimerReset && onTimerReset();
  };
  const handleResendCodeError = error => {
    onResendCodeError && onResendCodeError(error);
    setState('error');
  };
  const { loading, resendOTP: resendOTPViaOkta } = useOktaClient({
    onSuccess: handleResendCodeSuccess,
    onError: handleResendCodeError,
  });

  const resendOTP = useCallback(
    async ({ resendFactor = 'sms' }) => {
      if (!resend) {
        setDoesCountDown(false);
        return resendOTPViaOkta(resendFactor ?? factor);
      }

      const result = await resend(resendFactor);
      if (result.success) {
        setState('success');
      } else {
        onResendCodeError && onResendCodeError(result.error);
        setState('error');
      }

      return result;
    },
    [factor, onResendCodeError, resend, resendOTPViaOkta],
  );

  const [seconds, resetTimer] = useCountdownTimer(defaultTimer, onTimedOut, doesCountDown);
  const countDownText = `${getMinutes(seconds)}:${getRemainingSeconds(seconds)}`;

  const handleResendCodeClick = useCallback(
    ({ evt, resendFactor }) => {
      evt?.preventDefault();

      if (state === 'pending') {
        return;
      }

      onResendCodeClick && onResendCodeClick();
      formData.resetForm({
        values: {
          verificationCode: '',
        },
        errors: {},
      });
      if (state === 'locked') {
        requestAfterLock.current = resendFactor;
        setState('pending');
      } else {
        resendOTP({ resendFactor });
      }
    },
    [formData, onResendCodeClick, requestAfterLock, resendOTP, state],
  );

  const handleResendSMSCodeClick = useCallback(
    evt => {
      updateStore({ oktaFactor: OKTA_SMS_FACTOR });
      evt?.preventDefault();

      handleResendCodeClick({ evt, resendFactor: OKTA_SMS_FACTOR });
    },
    [handleResendCodeClick, updateStore],
  );

  const handleResendEmailCodeClick = useCallback(
    evt => {
      updateStore({ oktaFactor: OKTA_EMAIL_FACTOR });
      evt?.preventDefault();

      handleResendCodeClick({ evt, resendFactor: OKTA_EMAIL_FACTOR });
    },
    [handleResendCodeClick, updateStore],
  );

  const clearTimer = useCallback(() => {
    clearTimeout(timeout.current);
    timeout.current = null;
  }, [timeout]);

  useEffect(() => {
    if (!timeout.current && (state === 'locked' || state === 'success' || state === 'error')) {
      timeout.current = setTimeout(() => {
        setState('ready');
        clearTimer();
        if (requestAfterLock.current) {
          resendOTP({ resendFactor: requestAfterLock.current });
          requestAfterLock.current = null;
        }
      }, resendMsecs);
    }
  }, [state, resendMsecs, requestAfterLock, resendOTP, clearTimer, factor, storeState.oktaFactor]);

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  const allowAlternateFactor = !storeState.applicationSubmitted && storeState.oktaProfileHealthy;
  const showAlternate = allowAlternateFactor && !['pending', 'success', 'error'].includes(state) && !loading;

  const originalFactorText = `Resend code to your ${factor === OKTA_SMS_FACTOR ? 'mobile' : 'email address'}`;
  const alternateFactorText = `send code to your ${factor === OKTA_SMS_FACTOR ? 'email address' : 'mobile'}`;

  const handleResend = useCallback(
    (e, resendCallback, setReady = false) => {
      if (DEV_FEATURES.OTP_SKIP) {
        setState('pending');
        setDoesCountDown(false);
        if (setReady && factor === OKTA_SMS_FACTOR) {
          updateStore({ oktaFactor: OKTA_EMAIL_FACTOR });
        } else if (setReady && factor === OKTA_EMAIL_FACTOR) {
          updateStore({ oktaFactor: OKTA_SMS_FACTOR });
        }

        setTimeout(() => {
          setState('success');
          resetTimer();
          setDoesCountDown(true);
          onTimerReset && onTimerReset();
        }, 2000);

        return;
      }
      if (setReady) {
        setState('ready');
      }
      resendCallback(e);
    },
    [factor, updateStore, onTimerReset, resetTimer, setState, setDoesCountDown],
  );

  const onResendToOriginalFactor = useCallback(
    e => {
      handleResend(e, factor === OKTA_SMS_FACTOR ? handleResendSMSCodeClick : handleResendEmailCodeClick);
    },
    [handleResend, factor, handleResendSMSCodeClick, handleResendEmailCodeClick],
  );

  const onResendToAlternateFactor = e => {
    handleResend(e, factor === OKTA_SMS_FACTOR ? handleResendEmailCodeClick : handleResendSMSCodeClick, true);
  };

  useEffect(() => {
    if (prevResending.current && !resending) {
      onTimerReset && onTimerReset();
    }
    if (!prevResending.current && resending) {
      onResendToOriginalFactor();
    }
    prevResending.current = resending;
  }, [clearTimer, onResendToOriginalFactor, onTimerReset, resending, resetTimer]);

  return (
    <BasisOneTimeCode
      state={state}
      loading={loading}
      onResendCodeClick={handleResendCodeClick}
      theme={theme}
      countDownText={countDownText}
      ResendOrAlternate={() =>
        isFeatureOn('smsEmailVerificationOptions') && (
          <>
            <Stack gap="2" direction="horizontal">
              <Text>Not received your code yet?</Text>
              <ResendButton
                loading={resending || loading}
                state={state}
                onClick={onResendToOriginalFactor}
                text={originalFactorText}
                showOr={showAlternate}
              />
            </Stack>

            {showAlternate && (
              <Stack gap="2" direction="horizontal">
                <Text>alternatively</Text>
                <ResendButton
                  loading={resending || loading}
                  state={state}
                  onClick={onResendToAlternateFactor}
                  text={alternateFactorText}
                />
              </Stack>
            )}
          </>
        )
      }
      {...rest}
    />
  );
}

function ResendButton({ loading, state, onClick, text, showOr }) {
  if (loading || state === 'pending') {
    return (
      <Text>
        <strong>Sending...</strong>
      </Text>
    );
  }

  if (state === 'success') {
    return (
      <Text color="conditional.positive.text">
        <strong>Code sent</strong>
      </Text>
    );
  }

  if (state === 'error') {
    return (
      <Text color="conditional.negative.text">
        <strong>Error sending, please wait...</strong>
      </Text>
    );
  }

  return (
    <Text textStyle="body2">
      <TextButton onClick={onClick}>{text}</TextButton>
      {showOr && ' or'}
    </Text>
  );
}
